/**
 * How a browser download tells the page it has begun (docs/10-ux-audit.md · UX-47).
 *
 * `<a download>` gives nothing back: the click returns at once and the screen changes only when the
 * response headers arrive — on a large PDF over a slow link, tens of seconds later. So a same-origin
 * media URL may carry a `dl` token, and `GET /api/media/*` echoes it back in this cookie the moment
 * it starts responding (with `:error` appended when it could not). The page polls for it. Shared by
 * the route and the hook; no React, no server-only, so both sides can import it.
 */
export const DOWNLOAD_COOKIE = 'freealbum_dl';

/** Tokens are opaque but short and plain, so a URL cannot smuggle anything else into the cookie. */
export const DOWNLOAD_TOKEN = /^[a-z0-9]{1,32}$/i;

export type DownloadSignal = 'ok' | 'error';

/** The signal for `token` in a `document.cookie` string, if the route has answered that request. */
export function readDownloadSignal(cookies: string, token: string): DownloadSignal | null {
  const entry = cookies.split('; ').find((part) => part.startsWith(`${DOWNLOAD_COOKIE}=`));
  if (!entry) return null;
  const value = decodeURIComponent(entry.slice(DOWNLOAD_COOKIE.length + 1));
  if (value === token) return 'ok';
  if (value === `${token}:error`) return 'error';
  return null;
}

/** The `Set-Cookie` value the media route answers a `dl` request with. Short-lived: it is a signal, not state. */
export function downloadSignalCookie(token: string, outcome: DownloadSignal): string {
  return `${DOWNLOAD_COOKIE}=${token}${outcome === 'error' ? ':error' : ''}; Path=/; Max-Age=120; SameSite=Lax`;
}
